import { useMemo, useState } from "react";
import { useLessonFinish } from "./useLessonFinish";
import { type PictureConfig } from "../engine/lessonTypes";
import { PictureContent } from "./PictureContent";
import styles from "./LessonView.module.css";

interface PictureLessonViewProps extends PictureConfig {
  onFinished: () => void;
}

function shuffle(length: number): number[] {
  const indices = Array.from({ length }, (_, i) => i);
  for (let i = indices.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [indices[i], indices[j]] = [indices[j], indices[i]];
  }
  return indices;
}

function stripTags(html: string): string {
  return html.replace(/<[^>]+>/g, "");
}

export function PictureLessonView({
  images,
  labels,
  onFinished,
}: PictureLessonViewProps) {
  const [order, setOrder] = useState(() => shuffle(images.length));
  const [questionIndex, setQuestionIndex] = useState(0);
  const [wrongPosition, setWrongPosition] = useState<number | null>(null);
  const { scheduleFinish } = useLessonFinish(onFinished, true);

  const question = useMemo(
    () => stripTags(labels[questionIndex] ?? ""),
    [labels, questionIndex],
  );

  const handleClick = (position: number): void => {
    if (order[position] === questionIndex) {
      setWrongPosition(null);
      const next = questionIndex + 1;
      if (next >= images.length) {
        setOrder(shuffle(images.length));
        setQuestionIndex(0);
        scheduleFinish();
      } else {
        setQuestionIndex(next);
      }
    } else {
      setWrongPosition(position);
    }
  };

  return (
    <div className={styles.lesson}>
      <p className={styles.question}>
        Which picture shows <code>{question}</code>?
      </p>
      <div className={styles.pictures}>
        {order.map((imageIndex, position) => (
          <div className={styles.pictureItem} key={position}>
            <button
              type="button"
              className={styles.pictureButton}
              style={
                wrongPosition === position
                  ? { borderColor: "var(--error-color)" }
                  : undefined
              }
              onClick={() => handleClick(position)}
            >
              <PictureContent
                source={images[imageIndex]}
                alt={stripTags(labels[imageIndex] ?? "")}
              />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
